import { AccountListResponseSchema, AccountWithMembersSchema, AccountSchema, CreateAccountResponseSchema, AccountInviteListResponseSchema, AccountInviteSchema, InviteDetailsSchema, AcceptInviteResponseSchema, ResendInviteResponseSchema, CompleteOnboardingResponseSchema, PracticeSettingsResponseSchema, ClientProjectReferenceSettingsResponseSchema, ResolveRateResponseSchema, ProvisionedAccountSummarySchema, AdministeredEntityListResponseSchema, ProvisionAdministeredEntityResponseSchema, UpdateBusinessEntityResponseSchema, FeeEarnerDefaultFeeAccountListResponseSchema, FeeEarnerDefaultFeeAccountSchema, } from '@packages/types';
import { z } from 'zod';
import { api, apiRaw, getApiUrl, getApiBridge, parseErrorResponse } from '../client.js';
export function listAccounts(params) {
    const query = new URLSearchParams();
    if (params?.cursor)
        query.append('cursor', params.cursor);
    if (params?.limit !== undefined)
        query.append('limit', params.limit.toString());
    const qs = query.toString();
    return api(qs ? `/accounts?${qs}` : '/accounts', AccountListResponseSchema);
}
export function createAccount(input) {
    return api('/accounts', CreateAccountResponseSchema, {
        method: 'POST',
        body: input,
    });
}
export function getAccount(id) {
    return api(`/accounts/${id}`, AccountWithMembersSchema);
}
export function completeOnboarding(accountId, input) {
    return api(`/accounts/${accountId}/onboarding`, CompleteOnboardingResponseSchema, {
        method: 'POST',
        body: input,
    });
}
export function updateAccount(id, input) {
    return api(`/accounts/${id}`, AccountSchema, {
        method: 'PATCH',
        body: input,
    });
}
/**
 * Set up a separate set of books for a client's legal entity that the firm
 * administers (a business rescue, an insolvency, a deceased estate). Returns the
 * entity's Account; switch into it with `selectAccount(account.id)`.
 *
 * Idempotent on the client: calling it again returns the same account. There is
 * no `idempotencyKey` to pass - see the route's comment for why the client in
 * the path is the stronger key.
 */
export function provisionAdministeredEntity(accountId, clientId, setup) {
    return api(`/accounts/${accountId}/clients/${clientId}/administered-entity`, ProvisionAdministeredEntityResponseSchema, {
        method: 'POST',
        body: setup,
    });
}
/** The entities whose books this account administers. */
export function listAdministeredEntities(accountId) {
    return api(`/accounts/${accountId}/administered-entities`, AdministeredEntityListResponseSchema);
}
export function updateAccountBusinessEntity(accountId, input) {
    return api(`/accounts/${accountId}/business-entity`, UpdateBusinessEntityResponseSchema, {
        method: 'PATCH',
        body: input,
    });
}
export function getPracticeSettings(accountId) {
    return api(`/accounts/${accountId}/practice-settings`, PracticeSettingsResponseSchema);
}
export function resolveRate(accountId, query) {
    const params = new URLSearchParams();
    if (query.clientProjectId)
        params.set('clientProjectId', query.clientProjectId);
    if (query.activityType)
        params.set('activityType', query.activityType);
    params.set('unitType', query.unitType);
    if (query.performedByUserId)
        params.set('performedByUserId', query.performedByUserId);
    return api(`/accounts/${accountId}/resolve-rate?${params.toString()}`, ResolveRateResponseSchema);
}
export function updatePracticeSettings(accountId, input) {
    return api(`/accounts/${accountId}/practice-settings`, PracticeSettingsResponseSchema, {
        method: 'PATCH',
        body: input,
    });
}
export function getClientProjectReferenceSettings(accountId, options) {
    // `previewPrefix` is a what-if for the settings form's live preview. Omit
    // to fall back to the configured prefix.
    const search = options?.previewPrefix === undefined
        ? ''
        : `?previewPrefix=${encodeURIComponent(options.previewPrefix)}`;
    return api(`/accounts/${accountId}/client-project-reference-settings${search}`, ClientProjectReferenceSettingsResponseSchema);
}
export function updateClientProjectReferenceSettings(accountId, input) {
    return api(`/accounts/${accountId}/client-project-reference-settings`, ClientProjectReferenceSettingsResponseSchema, {
        method: 'PATCH',
        body: input,
    });
}
export async function removeMember(accountId, userId) {
    await apiRaw(`/accounts/${accountId}/members/${userId}`, {
        method: 'DELETE',
    });
}
export async function updateMemberRole(accountId, userId, input) {
    await apiRaw(`/accounts/${accountId}/members/${userId}`, {
        method: 'PATCH',
        body: input,
    });
}
export function listInvites(accountId, params) {
    const query = new URLSearchParams();
    if (params?.cursor)
        query.append('cursor', params.cursor);
    if (params?.limit !== undefined)
        query.append('limit', params.limit.toString());
    const qs = query.toString();
    return api(qs ? `/accounts/${accountId}/invites?${qs}` : `/accounts/${accountId}/invites`, AccountInviteListResponseSchema);
}
export function createInvite(accountId, input) {
    return api(`/accounts/${accountId}/invites`, AccountInviteSchema, {
        method: 'POST',
        body: input,
    });
}
export async function cancelInvite(accountId, inviteId) {
    await apiRaw(`/accounts/${accountId}/invites/${inviteId}`, {
        method: 'DELETE',
    });
}
export function resendInvite(accountId, inviteId) {
    return api(`/accounts/${accountId}/invites/${inviteId}/resend`, ResendInviteResponseSchema, {
        method: 'POST',
        body: {},
    });
}
export function getInviteByToken(token) {
    return api(`/invites/${token}`, InviteDetailsSchema, { skipAuth: true });
}
/**
 * Upload or replace the account logo.
 * Uses raw fetch with FormData – the api() wrapper is JSON-only.
 */
export async function uploadAccountLogo(accountId, file) {
    const bridge = getApiBridge();
    const baseUrl = getApiUrl();
    const token = bridge.getToken();
    const formData = new FormData();
    formData.append('file', file);
    const headers = {};
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${baseUrl}/accounts/${accountId}/logo`, {
        method: 'POST',
        headers,
        body: formData,
    });
    if (response.status === 401) {
        bridge.removeToken();
        bridge.onUnauthorized();
        throw new Error('Session expired');
    }
    if (!response.ok) {
        throw await parseErrorResponse(response);
    }
    const json = await response.json();
    const result = AccountSchema.safeParse(json);
    if (!result.success) {
        throw new Error('Invalid response from server');
    }
    return result.data;
}
export async function deleteAccountLogo(accountId) {
    await apiRaw(`/accounts/${accountId}/logo`, {
        method: 'DELETE',
    });
}
export async function getAccountLogoBlob(accountId, version) {
    // Cache-bust on the account's updatedAt: the logo route is served with a 1-hour
    // browser cache at a stable URL, so without a version param a freshly uploaded
    // logo is served stale from cache. uploadAccountLogo bumps updatedAt, so a new
    // version yields a cache miss and the fresh image; unchanged logos stay cached.
    const query = version ? `?v=${encodeURIComponent(version)}` : '';
    const response = await apiRaw(`/accounts/${accountId}/logo${query}`);
    return response.blob();
}
export function getAccountSummary(accountId) {
    return api(`/accounts/${accountId}/summary`, ProvisionedAccountSummarySchema);
}
export function updateMemberHourlyRate(accountId, userId, input) {
    return api(`/accounts/${accountId}/members/${userId}/hourly-rate`, z.object({ success: z.boolean() }), { method: 'PATCH', body: input });
}
export function updateMemberEmailRate(accountId, userId, input) {
    return api(`/accounts/${accountId}/members/${userId}/email-rate`, z.object({ success: z.boolean() }), { method: 'PATCH', body: input });
}
export function acceptInvite(input) {
    return api('/invites/accept', AcceptInviteResponseSchema, {
        method: 'POST',
        body: input,
    });
}
export function acceptInviteWithPassword(input) {
    return api('/invites/accept-with-password', AcceptInviteResponseSchema, {
        method: 'POST',
        body: input,
    });
}
/** Effective default fee income account for each current fee earner (null = firm default). */
export function listFeeEarnerFeeAccounts(accountId) {
    return api(`/accounts/${accountId}/fee-earner-fee-accounts`, FeeEarnerDefaultFeeAccountListResponseSchema);
}
/** Sets a fee earner's default fee income account; `ledgerAccountId: null` clears it. */
export function setMemberDefaultFeeAccount(accountId, userId, input) {
    return api(`/accounts/${accountId}/members/${userId}/default-fee-account`, FeeEarnerDefaultFeeAccountSchema, {
        method: 'PATCH',
        body: input,
    });
}
